import { useState, useMemo, useEffect } from 'react';
import { FinanceReport } from '../../types/financeReport';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { PeriodSelector, PeriodRange } from '../ui/PeriodSelector';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Legend,
} from 'recharts';
import { useWindowSize } from '../../hooks/useWindowSize';
import { formatNumber } from '../../utils/format';
import { TrendingUp } from 'lucide-react';

interface FinanceTrendChartProps {
  reports: FinanceReport[];
  onRangeChange?: (range: { from: Date | null; to: Date | null; label: string }) => void;
}

const CATEGORIES = [
  { id: 'global', title: 'Vue globale' },
  { id: 'tithes', title: 'Dîmes' },
  { id: 'offerings', title: 'Offrandes' },
] as const;

type CategoryId = typeof CATEGORIES[number]['id'];

export function FinanceTrendChart({ reports, onRangeChange }: FinanceTrendChartProps) {
  const [selectedCategory, setSelectedCategory] = useState<CategoryId>('global');
  const [range, setRange] = useState<PeriodRange>({ from: null, to: null, label: '' });
  const { width } = useWindowSize();
  const isMobile = width < 768;

  useEffect(() => {
    onRangeChange?.(range);
  }, [range, onRangeChange]);

  const filteredReports = useMemo(() => {
    const { from, to } = range;
    return reports
      .filter((r) => {
        const d = new Date(r.date);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [reports, range]);

  // Agrégation par mois (cumul mensuel)
  const chartData = useMemo(() => {
    const map = new Map<string, {
      key: string;
      date: string;
      tithes: number;
      regularOfferings: number;
      specialOfferings: number;
      total: number;
    }>();

    filteredReports.forEach((r) => {
      const d = new Date(r.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = format(d, isMobile ? 'MMM yy' : 'MMMM yyyy', { locale: fr });
      const existing = map.get(key) ?? {
        key,
        date: label,
        tithes: 0,
        regularOfferings: 0,
        specialOfferings: 0,
        total: 0,
      };
      existing.tithes += r.finances.tithes;
      existing.regularOfferings += r.finances.regularOfferings;
      existing.specialOfferings += r.finances.specialOfferings;
      existing.total += r.totalFinances;
      map.set(key, existing);
    });

    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [filteredReports, isMobile]);

  const yTickFormatter = (value: number) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${Math.round(value / 1_000)}k`;
    return `${value}`;
  };

  const tooltipFormatter = (value: number) => `${formatNumber(value)} FCFA`;

  const commonAxisProps = {
    tick: { fill: '#4B5563', fontSize: isMobile ? 10 : 12 },
  };

  const margin = {
    top: 5,
    right: isMobile ? 10 : 30,
    left: isMobile ? 0 : 20,
    bottom: isMobile ? 40 : 25,
  };

  const renderChart = () => {
    if (selectedCategory === 'global') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={margin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" {...commonAxisProps} angle={-45} textAnchor="end" height={60} />
            <YAxis {...commonAxisProps} width={isMobile ? 40 : 55} tickFormatter={yTickFormatter} />
            <Tooltip
              formatter={tooltipFormatter}
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '0.5rem',
                fontSize: isMobile ? 12 : 14,
              }}
            />
            <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
            <Bar dataKey="tithes" stackId="a" fill="#10B981" name="Dîmes" barSize={isMobile ? 15 : 22} />
            <Bar dataKey="regularOfferings" stackId="a" fill="#3B82F6" name="Offrandes ord." barSize={isMobile ? 15 : 22} />
            <Bar dataKey="specialOfferings" stackId="a" fill="#F59E0B" name="Offrandes spé." barSize={isMobile ? 15 : 22} />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#8B5CF6"
              strokeWidth={2}
              dot={{ fill: '#8B5CF6', r: isMobile ? 3 : 4 }}
              name="Total"
            />
          </ComposedChart>
        </ResponsiveContainer>
      );
    }

    if (selectedCategory === 'tithes') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={margin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" {...commonAxisProps} angle={-45} textAnchor="end" height={60} />
            <YAxis {...commonAxisProps} width={isMobile ? 40 : 55} tickFormatter={yTickFormatter} />
            <Tooltip
              formatter={tooltipFormatter}
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '0.5rem',
                fontSize: isMobile ? 12 : 14,
              }}
            />
            <Line
              type="monotone"
              dataKey="tithes"
              stroke="#10B981"
              strokeWidth={2}
              dot={{ fill: '#10B981', r: isMobile ? 3 : 4 }}
              name="Dîmes"
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    // offerings
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={margin}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" {...commonAxisProps} angle={-45} textAnchor="end" height={60} />
          <YAxis {...commonAxisProps} width={isMobile ? 40 : 55} tickFormatter={yTickFormatter} />
          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '0.5rem',
              fontSize: isMobile ? 12 : 14,
            }}
          />
          <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
          <Bar dataKey="regularOfferings" fill="#3B82F6" name="Offrandes ordinaires" barSize={isMobile ? 15 : 22} />
          <Bar dataKey="specialOfferings" fill="#F59E0B" name="Offrandes spéciales" barSize={isMobile ? 15 : 22} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-5 w-5 text-[#00665C]" />
        <h3 className="text-lg font-semibold text-gray-800">Évolution des finances</h3>
      </div>

      {/* Catégories */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1">
        <div className="flex gap-2 min-w-max">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedCategory(c.id)}
              className={`px-3 py-1.5 md:px-3 md:py-2 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === c.id
                  ? 'bg-[#00665C] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {c.title}
            </button>
          ))}
        </div>
      </div>

      {/* Filtres période */}
      <PeriodSelector defaultPreset="3m" onChange={setRange} className="mb-4" />

      <div className="h-[280px] md:h-[380px] w-full">
        {chartData.length > 0 ? (
          renderChart()
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p>Aucune donnée disponible pour cette période</p>
          </div>
        )}
      </div>
    </div>
  );
}
